// covered-by: qa/rickrubin/tests/specs/cca/file.downloads.open-ticket.spec.ts
import { Flex, Grid } from "@radix-ui/themes";
import { usePostHog } from "posthog-js/react";
import {
  createContext,
  type RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { type Editor } from "tldraw";

import TicketCanvas from "./components/ticket-canvas";
import TicketHeader from "./components/TicketHeader";
import { query as commentsQuery, ticketRoute } from "./route";

import { ShareLinkInviteToast } from "../../../../components/ShareLinkInviteToast";
import { useUI } from "../../../../contexts/ui";
import { useRateTicket } from "../../../../hooks/useRateTicket";
import { shareLinkClaim } from "../../../../services/auth";
import { fetchTicketComments } from "../../../../services/projects";
import { isNil } from "../../../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { setComments } from "../../../../utils/slices/ticket";
import { CommentDrawer } from "../components/comment-drawer";
import { RepliesCollapseProvider } from "../components/comment-drawer/Comment";

type TicketContextValues = {
  /**
   * Whether or not the user has initiated an AI Review from anywhere on the
   * page.
   */
  hasInitiatedAiReview: boolean;
  /**
   * Handler to set whether the user has initiated an AI Review
   */
  setHasInitiatedAiReview(value: boolean): void;
  /**
   * The TLDraw Editor object, used by other components on the page, such as
   * `CommentDrawer`, and to create helper functions to navigate to shapes (e.g.
   * if the user clicks a page in the document thumbnail sidebar in doc mode).
   * This value must only be set by a child descendant that implements TLDraw
   * and no other component should set this value. If no TLDraw instance has
   * been loaded, `editor` will be `undefined`.
   */
  editor?: Editor;
  /**
   * Sets the editor object. It is important to remember that only TLDraw
   * should set this value where it is implemented. There should not be multiple
   * references to TLDraw on the page, and this value must not be set by anything
   * other than one instance of TLDraw.
   */
  setEditor(editor?: Editor): void;
  /**
   * Some to-be-removed controls for video mode.
   */
  videoRef: RefObject<{
    handleCommentClicked: (_seekTime: number) => void;
    duration: number;
  }>;
};

const TicketContext = createContext({} as TicketContextValues);
export const useTicket = () => useContext(TicketContext);

export default function View() {
  // Router methods
  const { ticketId } = ticketRoute.useParams();
  const { rating, integration } = ticketRoute.useSearch();

  // Slices
  const { value: ticket, isLoading } = useAppSelector((state) => state.ticket);
  const authedUser = useAppSelector((state) => state.auth.value);

  // Mutations
  const rateTicketMutation = useRateTicket();

  // Analytics
  const posthog = usePostHog();
  const { notify } = useUI();

  // Redux
  const dispatch = useAppDispatch();

  // Local state
  const [hasInitiatedAiReview, setHasInitiatedAiReview] = useState(false);
  const [editor, setEditor] = useState<Editor>();
  // TODO: remove this because refs and imperative handlers abstract the code
  // in ways that reduce readability and make the code harder to understand.
  const videoRef = useRef<{
    handleCommentClicked: (_seekTime: number) => void;
    duration: number;
  }>(null);

  // Effect chain

  useEffect(() => {
    const context = {
      ticketId,
      ticketTitle: ticket?.title,
    };
    posthog.register(context);

    return () => {
      Object.keys(context).forEach((key) => {
        posthog.unregister(key);
      });
    };
  }, []);

  // Handle rating if provided in URL
  useEffect(() => {
    if (rating && ticketId && rating.toString() != ticket?.rating?.toString()) {
      const ratingNum = parseInt(rating.toString(), 10);
      if (!isNaN(ratingNum) && ratingNum >= 1 && ratingNum <= 10) {
        rateTicketMutation.mutate({ ticketId, rating: ratingNum });
        posthog.capture(ratingNum > 1 ? "positive_rating" : "negative_rating", {
          ticketId,
          rating: ratingNum,
        });
        notify({
          title: "Thank you for your feedback!",
          message: "We'll use it to improve our service.",
        });
      }
    }
  }, [rating, ticketId]);

  // Handle integration if provided in URL
  useEffect(() => {
    if (integration && ticketId) {
      posthog.capture("ticket_opened_from_integration", {
        ticketId,
        ticketTitle: ticket?.title,
      });
    }
  }, [integration, ticketId]);

  // Share-link tracking + INVITED-user auto-claim. Fires once per session per
  // ticketId so we can answer "is this temp account hopping between share
  // links / coming back to the same one?". An authenticated INVITED user
  // (the share-link email-only flow minted them a real JWT but they haven't
  // set a password yet) gets idempotently allow-listed onto whatever ticket
  // they land on next without having to re-enter their email.
  useEffect(() => {
    if (!ticketId) return;

    const guestTokenForTicket = sessionStorage.getItem(`${ticketId}|token`);
    const isGuestSession = Boolean(authedUser?.referredBy && !authedUser.email);
    const isAuthenticatedInvited = Boolean(
      authedUser?.userID && authedUser?.isEnterpriseOnboarded === false,
    );
    const isAllowListed = Boolean(
      authedUser?.userID && authedUser?.isEnterpriseOnboarded,
    );

    const viewedKey = `puntt.share-link-viewed.${ticketId}`;
    if (
      (isGuestSession || isAuthenticatedInvited) &&
      !sessionStorage.getItem(viewedKey)
    ) {
      sessionStorage.setItem(viewedKey, "1");
      posthog.capture("share_link_ticket_viewed", {
        ticketId,
        enterpriseId: authedUser?.enterpriseId,
        isAuthenticated: Boolean(authedUser?.userID),
        userOnboardingStatus: isAuthenticatedInvited
          ? "INVITED"
          : isAllowListed
            ? "ONBOARDED"
            : "GUEST",
        isAllowListed,
      });
    }

    // Auto-claim path: an INVITED user opening a NEW share link (different
    // ticketId than the one their JWT was minted for). The fresh guest
    // token sits in sessionStorage from the auth slice's `derive` action,
    // and we POST it to /share-link-claim so the user gets allow-listed
    // onto this ticket without having to re-enter their email.
    if (isAuthenticatedInvited && guestTokenForTicket && authedUser?.userID) {
      const claimedKey = `puntt.share-link-claimed.${ticketId}`;
      if (sessionStorage.getItem(claimedKey)) return;

      shareLinkClaim({ guestToken: guestTokenForTicket })
        .then((response) => {
          sessionStorage.setItem(claimedKey, "1");
          if (response && !response.alreadyAllowListed) {
            posthog.capture("share_link_ticket_claimed", {
              ticketId,
              enterpriseId: authedUser?.enterpriseId,
              userOnboardingStatus: "INVITED",
            });
            // The claim just allow-listed this user onto the ticket.
            // Refetch comments so any role-based view changes that hang
            // off allow-list membership reconcile without a full reload.
            fetchTicketComments({ ticketId, query: commentsQuery })
              .then((fresh) => dispatch(setComments(fresh)))
              .catch(() => {
                // Best-effort; the next route load will reconcile.
              });
          }
        })
        .catch(() => {
          // Best-effort; if claim fails the user falls back to the existing
          // allow-list/redaction path. Don't surface the error.
        });
    }
  }, [
    ticketId,
    authedUser?.userID,
    authedUser?.isEnterpriseOnboarded,
    authedUser?.enterpriseId,
    authedUser?.referredBy,
    authedUser?.email,
    dispatch,
  ]);

  /////
  // No hooks beyond this point
  /////

  // If for some reason the API responded with a nullish ticket, or the parser
  // failed and return undefined.

  if (isNil(ticket)) {
    if (isLoading) {
      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          height="100vh"
          width="100vw"
          position="fixed"
          className="text-center"
        >
          Loading&hellip;
        </Flex>
      );
    }
    throw { status: 404 };
  }

  return (
    <TicketContext.Provider
      value={{
        hasInitiatedAiReview,
        setHasInitiatedAiReview,
        editor,
        setEditor,
        videoRef,
      }}
    >
      <RepliesCollapseProvider>
        <ShareLinkInviteToast
          ticketId={ticketId}
          enterpriseId={authedUser?.enterpriseId}
        />
        <Grid
          className="h-screen grid-cols-[1fr_auto] grid-rows-[auto_1fr] overflow-hidden [&:has([data-mode=video])]:bg-puntt-neutral-gray-5"
          data-testid="ticket-layout"
        >
          <TicketHeader />
          <TicketCanvas />
          <CommentDrawer editor={editor} videoRef={videoRef} />
        </Grid>
      </RepliesCollapseProvider>
    </TicketContext.Provider>
  );
}
